import { useQuery } from "@tanstack/react-query";
import { Calendar } from "lucide-react";
import { useParams } from "react-router-dom";
import { GetBlog, GetCaseStudy } from "@/components/api/queries";
import { z } from "zod";
import { schemaBlogItem } from "@/components/api/zod-types";
import LoadingSkeleton from "@/components/LoadingSkeleton/LoadingSkeleton";
import { NotFound } from "@/components/pages/ErrorPages/Status";
import { useLocation } from "react-router-dom";
import { useLayoutEffect, useEffect, useState, useRef } from "react";
import SocialShare from "@/components/pages/Resources/SocialShare";
import { Helmet } from "react-helmet";
import { safeDecodeURIComponent } from "@/lib/utils";
import ContactFormSheet from "@/components/sliders/ContactFormSheet";



const styles = {
    img: {
        maxWidth: "70%",
        height: "30vh",
        marginTop: "20px",
        marginBottom: "20px",
        borderRadius: "10px",
    },
};

function formatDateToNZ(dateStr?: string) {
    if (!dateStr) return "";
    let d = new Date(dateStr);
    if (isNaN(d.getTime())) {
        // try parse common US format MM/DD/YYYY (take first space-separated token)
        const token = dateStr.split(" ")[0];
        const parts = token.split("/");
        if (parts.length === 3) {
            const month = Number(parts[0]);
            const day = Number(parts[1]);
            const year = Number(parts[2]);
            if (!Number.isNaN(month) && !Number.isNaN(day) && !Number.isNaN(year)) {
                d = new Date(year, month - 1, day);
            } else {
                return dateStr;
            }
        } else {
            return dateStr;
        }
    }
    return d.toLocaleDateString("en-NZ", { timeZone: "Pacific/Auckland" });
}


export default function BlogItem() {
    const location = useLocation();

    // Infer type from the current route
    // const isCaseStudy = location.pathname.startsWith("/casestudies/");
    const isCaseStudy = location.pathname.startsWith("/case-studies/");
    const type = isCaseStudy ? "casestudy" : "blog";

    useLayoutEffect(() => {
        // scroll window (covers normal pages)
        window.scrollTo(0, 0);

        // also reset any scrollable containers used in the app/layout
        const selectors = [
            ".overflow-y-auto",
            "[data-scrollable]",
            ".scrollable-root",
            ".app-scroll-container"
        ];
        selectors.forEach(sel => {
            document.querySelectorAll(sel).forEach(el => {
                try { (el as HTMLElement).scrollTop = 0; } catch { /* ignore */ }
            });
        });
    }, [location.pathname]);

    const { id } = useParams<{ id: string }>();

    // const { data: blogItem, isLoading, isFetching, error } = useQuery({
    //     queryKey: ["blogItem", id],
    //     staleTime: 1000 * 60 * 60 * 24,
    //     gcTime: 1000 * 60 * 60 * 24,
    //     queryFn: async () => {
    //         const res = await GetBlog(id!);
    //         try {
    //             // Validate response with Zod schema
    //             return schemaBlogItem.parse(res.data);
    //         } catch (err) {
    //             if (err instanceof z.ZodError) {
    //                 throw new Error("Invalid blog data received from server");
    //             }
    //             throw err;
    //         }
    //     }
    // });

    const { data: blogItem, isLoading, isFetching, error } = useQuery({
        queryKey: ["blogItem", id],
        staleTime: 1000 * 60 * 60 * 24,
        gcTime: 1000 * 60 * 60 * 24,
        queryFn: async () => {
            let res = await GetBlog(id!);
            // If not found, try case study category
            const hasResult = "result" in res.data && res.data.result;
            const hasResults = "results" in res.data && Array.isArray(res.data.results) && res.data.results.length > 0;
            if (
                (!hasResult && !hasResults) ||
                res.data.statuscode === 404
            ) {
                res = await GetCaseStudy(id!);
            }
            // Normalize: if result exists, use it; if results array, use first item
            let singleResult;
            if ("result" in res.data && res.data.result) {
                singleResult = res.data;
            } else if ("results" in res.data && Array.isArray(res.data.results) && res.data.results.length > 0) {
                singleResult = { ...res.data, result: res.data.results[0] };
            } else {
                singleResult = { ...res.data, result: null };
            }
            try {
                return schemaBlogItem.parse(singleResult);
            } catch (err) {
                if (err instanceof z.ZodError) {
                    throw new Error("Invalid blog data received from server");
                }
                throw err;
            }
        }
    });

    // 17/2/2026 - decode blog content and replace + with space (common URL encoding issue)
    // const html = decodeURIComponent(blogItem?.result.copy || "").replace(/\+/g, " ");
    const html = safeDecodeURIComponent(blogItem?.result.copy || "").replace(/\+/g, " ");

    // Browser tab title update
    useEffect(() => {
        if (!blogItem?.result?.title) return;
        const prev = document.title;
        // document.title = decodeHtmlEntities(decodeURIComponent(blogItem.result.title));
        document.title = decodeHtmlEntities(safeDecodeURIComponent(blogItem.result.title));
        return () => { document.title = prev; };
    }, [blogItem]);
    // end browser tab title update
    // 18/5/26
    const [contactOpen, setContactOpen] = useState(false);
    const blogContentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = blogContentRef.current;
        if (!el) return;
        const handler = (e: MouseEvent) => {
            const target = (e.target as HTMLElement).closest('[data-action="open-contact-form"]');
            if (target) {
                e.preventDefault();
                setContactOpen(true);
            }
        };
        el.addEventListener('click', handler);
        return () => el.removeEventListener('click', handler);
    }, [html]);

    if (isLoading || isFetching) {
        return <LoadingSkeleton />;
    }

    if (error) {
        return <NotFound />;
    }

    function decodeHtmlEntities(input?: string) {
        if (!input) return "";
        const txt = typeof document !== "undefined" ? document.createElement("textarea") : null;
        if (!txt) return input;
        txt.innerHTML = input;
        return txt.value;
    }



    return (
        <>
            {/* 20260330: added structured data for SEO */}
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Article",
                        "headline": decodeHtmlEntities(safeDecodeURIComponent(blogItem?.result.title || "")),
                        "description": decodeHtmlEntities(safeDecodeURIComponent(blogItem?.result.intro || "")),
                        "url": typeof window !== "undefined"
                            ? window.location.href
                            : `https://www.harveynormancommercial.nz/blogs/${blogItem?.result.url}`,
                        "image": blogItem?.result.imageurl,
                        "publisher": {
                            "@type": "Organization",
                            "name": "Harvey Norman Commercial NZ",
                            "logo": {
                                "@type": "ImageObject",
                                "url": "https://hncnzwww.blob.core.windows.net/www/pwa/icons/pwa_hnc_logo.svg"
                            }
                        },
                        "author": {
                            "@type": "Organization",
                            "name": "Harvey Norman Commercial NZ"
                        }
                    })}
                </script>
            </Helmet>
            <div className=" bg-white pt-8  flex flex-col w-full  ">
                {/* <div className="bg-white flex flex-col w-full blog-main-content">  */}
                <div className="max-w-[900px] mx-auto w-full px-4 lg:px-0 pt-16">
                    <div className=" flex justify-center md:justify-end mb-4   ">
                        {/* <SocialShare
                        className="self-end "
                        url={typeof window !== "undefined"
                            ? window.location.href
                            : `https://www.harveynormancommercial.nz/blogs/${blogItem?.result.url}`}
                        title={decodeHtmlEntities(decodeURIComponent(blogItem?.result.title || ""))}
                    /> */}
                        {/* <SocialShare
                        className="self-end "
                        url={typeof window !== "undefined"
                            ? window.location.href
                            : `https://www.harveynormancommercial.nz/${type === "casestudy" ? "casestudies" : "blog"}/${blogItem?.result.url}`}
                        title={decodeHtmlEntities(decodeURIComponent(blogItem?.result.title || ""))}
                    /> */}
                        <SocialShare
                            className="self-end "
                            url={typeof window !== "undefined"
                                ? window.location.href
                                : `https://www.harveynormancommercial.nz/${type === "casestudy" ? "case-studies" : "blogs"}/${blogItem?.result.url}`}
                            title={decodeHtmlEntities(safeDecodeURIComponent(blogItem?.result.title || ""))}
                        />
                    </div>


                    <div className="flex flex-col md:space-x-8 bg-white w-full overflow-y-auto scrollbar-hide pb-0 lg:pb-4 xl:pb-8">
                        <div className="max-w-[900px] mx-auto w-full">
                            <div>
                                <h1 className="text-3xl font-bold mb-4 text-black w-full" style={{ fontFamily: 'Avenir, Arial, sans-serif' }}>{decodeHtmlEntities(safeDecodeURIComponent(blogItem?.result.title!))}</h1>
                                {/* max-w-4xl */}
                                <p className="text-gray-600 mb-4 w-full" style={{ fontFamily: 'Avenir, Arial, sans-serif' }}>{decodeHtmlEntities(safeDecodeURIComponent(blogItem?.result.intro!))}</p>
                                <div className="flex items-center justify-items-center mb-4 gap-4 lg:gap-12">
                                    <p className="text-gray-600 flex items-center space-x-4" style={{ fontFamily: 'Avenir, Arial, sans-serif' }}>
                                        <Calendar />
                                        <span>{formatDateToNZ(blogItem?.result.date)}</span>
                                    </p>


                                </div>
                            </div>
                            {/* max-w-3xl */}
                            <div className="">
                                {/* <img
                                    src={blogItem?.result.imageurl}
                                    alt={blogItem?.result.id}
                                    className="w-full  h-[35vh] lg:h-[65vh] lg:max-h-[65vh] xl:h-[45vh] rounded object-cover"
                                /> */}

                                <img
                                    src={blogItem?.result.imageurl}
                                    alt={blogItem?.result.id}
                                    className="w-full  aspect-[1500/850] rounded object-cover"
                                />
                            </div>
                        </div>
                    </div>
                    {/* Blog HTML content container: already HTML, includes images, etc. */}
                    {/* <div className="blog-content-container">
                    <div className="blog-content" dangerouslySetInnerHTML={{ __html: decodeURIComponent(blogItem?.result.copy!) }} />
                </div> */}

                    {/* 17/2/2026 */}
                    {/* <div className="blog-content" dangerouslySetInnerHTML={{ __html: html }} /> */}
                    {/* 18/5/26 */}
                    <div ref={blogContentRef} className="blog-content" dangerouslySetInnerHTML={{ __html: html }} />
                    <ContactFormSheet open={contactOpen} onOpenChange={setContactOpen} hideTrigger  />
                </div>




            </div>
        </>
    );

}

